import { useState, useRef, useCallback } from 'react'
import { X, Upload } from 'lucide-react'
import {
  IntuiTabs,
  IntuiTabsList,
  IntuiTabsTrigger,
} from '@/components/intueri/IntuiTabs'
import { IntuiButton } from '@/components/intueri/IntuiButton'
import { useTranslation } from '@/i18n'

const PRESET_AVATARS = [
  '/images/avatars/avatar-light-01.svg',
  '/images/avatars/avatar-light-02.svg',
  '/images/avatars/avatar-light-03.svg',
  '/images/avatars/avatar-light-04.svg',
  '/images/avatars/avatar-light-05.svg',
  '/images/avatars/avatar-light-06.svg',
  '/images/avatars/avatar-light-07.svg',
  '/images/avatars/avatar-light-08.svg',
  '/images/avatars/avatar-dark-01.svg',
  '/images/avatars/avatar-dark-02.svg',
  '/images/avatars/avatar-dark-03.svg',
  '/images/avatars/avatar-dark-04.svg',
  '/images/avatars/avatar-dark-05.svg',
  '/images/avatars/avatar-dark-06.svg',
  '/images/avatars/avatar-dark-07.svg',
  '/images/avatars/avatar-dark-08.svg',
]

interface AvatarPickerProps {
  open: boolean
  onClose: () => void
  onSelectPreset: (url: string) => void
  onUploadFile: (file: File) => void
  selectedUrl?: string | null
}

export default function AvatarPicker({ open, onClose, onSelectPreset, onUploadFile, selectedUrl }: AvatarPickerProps) {
  const { t } = useTranslation()
  const [tab, setTab] = useState('select')
  const [isDragOver, setIsDragOver] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleFile = useCallback((file: File) => {
    if (!file.type.startsWith('image/')) return
    onUploadFile(file)
    onClose()
  }, [onUploadFile, onClose])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)
    const file = e.dataTransfer.files[0]
    if (file) handleFile(file)
  }, [handleFile])

  if (!open) return null

  return (
    <div className="absolute left-0 top-full z-50 mt-2 w-[380px] rounded-lg border border-[#e5e5e5] bg-white shadow-lg dark:border-dark-border dark:bg-dark-card">
      {/* Header */}
      <div className="flex items-center justify-between px-3 pt-3">
        <IntuiTabs value={tab} onValueChange={setTab}>
          <IntuiTabsList variant="line">
            <IntuiTabsTrigger value="select">{t('digital_coworker:avatarPicker.selectTab', { defaultValue: 'Select Avatar' })}</IntuiTabsTrigger>
            <IntuiTabsTrigger value="upload">{t('digital_coworker:avatarPicker.uploadTab', { defaultValue: 'Upload' })}</IntuiTabsTrigger>
          </IntuiTabsList>
        </IntuiTabs>
        <IntuiButton variant="ghost" size="icon-xs" onClick={onClose}>
          <X />
        </IntuiButton>
      </div>

      {/* Content */}
      <div className="p-3">
        {tab === 'select' ? (
          <div className="grid grid-cols-4 gap-3">
            {PRESET_AVATARS.map((url, i) => (
              <button
                key={`${url}-${i}`}
                type="button"
                onClick={() => { onSelectPreset(url); onClose() }}
                className={`flex size-[80px] items-center justify-center overflow-hidden rounded border bg-[#F6F6F6] pt-[19px] transition-all hover:border-[#0e7490] ${
                  selectedUrl === url ? 'border-[#0e7490] border-2' : 'border-[#C4C4C4]'
                }`}
              >
                <img src={url} alt={t('digital_coworker:avatarPicker.avatarAlt', { number: i + 1, defaultValue: 'Avatar {{number}}' })} className="size-20 object-contain" />
              </button>
            ))}
          </div>
        ) : (
          <div
            className={`flex min-h-[360px] flex-col items-center justify-center rounded-lg border-2 border-dashed transition-colors cursor-pointer ${
              isDragOver ? 'border-[#0e7490] bg-[#0e7490]/5' : 'border-[#22d3ee] dark:border-[#22d3ee]'
            }`}
            onDragOver={(e) => { e.preventDefault(); setIsDragOver(true) }}
            onDragLeave={() => setIsDragOver(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <Upload className="size-6 text-muted-foreground mb-2" />
            <p className="text-sm text-muted-foreground text-center px-4">
              {t('digital_coworker:avatarPicker.dropHint', { defaultValue: 'Drag and drop files here, or click to select files' })}
            </p>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0]
                if (file) handleFile(file)
                e.target.value = ''
              }}
            />
          </div>
        )}
      </div>
    </div>
  )
}
